import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  nuloSeNaoEncontrado,
  type PaginaComPermissoes,
} from '@/modulos/operacao/cadastros/leitura';
import { parametrosDaListagem } from '@/modulos/operacao/comum/contrato';
import { naoInsistirEmRecusa } from '@/modulos/operacao/comum/dados';
import { chamarApi, type ErroApi } from '@/nucleo/auth/cliente-api';
import { exportarCsv } from '@/nucleo/exportarCsv';
import { mascaraCpf } from '@/nucleo/formato/documentos';

import type { Condutor, CondutorDetalhe, FiltroDeCondutores } from './contrato';

/**
 * A camada de dados dos condutores: lista, detalhe e exportação, lidos da API
 * (`apps/api/src/modulos/condutores`). Só leitura — cadastro e mudança são da
 * J-05. O CPF chega inteiro, só dígitos; a tela e o CSV formatam.
 */

export const CAMINHO_DA_API = '/bff/v1/condutores';

export const CHAVES_DE_CONDUTORES = {
  lista: (filtro: FiltroDeCondutores) => ['condutores', 'lista', filtro] as const,
  exportacao: (filtro: Omit<FiltroDeCondutores, 'pagina' | 'tamanho'>) =>
    ['condutores', 'exportacao', filtro] as const,
  detalhe: (slug: string) => ['condutores', 'detalhe', slug] as const,
};

/** Teto da exportacao: o mesmo da API (`TETO_DA_EXPORTACAO`). */
export const TETO_DA_EXPORTACAO = 1000;

export function useCondutores(
  filtro: FiltroDeCondutores,
): UseQueryResult<PaginaComPermissoes<Condutor>, ErroApi> {
  return useQuery<PaginaComPermissoes<Condutor>, ErroApi>({
    queryKey: CHAVES_DE_CONDUTORES.lista(filtro),
    queryFn: ({ signal }) =>
      chamarApi<PaginaComPermissoes<Condutor>>(
        `${CAMINHO_DA_API}${parametrosDaListagem({ ...filtro })}`,
        { sinal: signal },
      ),
    placeholderData: (anterior) => anterior,
    retry: naoInsistirEmRecusa,
  });
}

/** O condutor de `/operacao/condutores/:slug`; `null` quando nao existe ou o recorte nao mostra. */
export function useCondutor(
  slug: string | undefined,
): UseQueryResult<CondutorDetalhe | null, ErroApi> {
  return useQuery<CondutorDetalhe | null, ErroApi>({
    queryKey: CHAVES_DE_CONDUTORES.detalhe(slug ?? ''),
    enabled: !!slug,
    queryFn: ({ signal }) =>
      chamarApi<CondutorDetalhe>(`${CAMINHO_DA_API}/${encodeURIComponent(slug ?? '')}`, {
        sinal: signal,
      }).catch(nuloSeNaoEncontrado),
    retry: naoInsistirEmRecusa,
  });
}

/**
 * Leitura sincrona por slug, que a tela de edicao (`CadastroEdicaoPage`) ainda
 * usa. Nao ha mais copia local: o detalhe vem da API (`useCondutor`), e a
 * edicao de cadastro fica fora desta rodada (so a J-05 muda condutor).
 */
export function obterCondutorPorSlug(_slug: string | undefined): Condutor | undefined {
  return undefined;
}

/** Baixa a listagem filtrada como CSV, pela rota de exportacao, que registra o acesso. */
export function useBaixarCondutoresCsv(): (filtro: FiltroDeCondutores) => Promise<number> {
  const consultas = useQueryClient();

  return async ({ pagina: _pagina, tamanho: _tamanho, ...recorte }: FiltroDeCondutores) => {
    const resposta = await consultas.fetchQuery<PaginaComPermissoes<Condutor>>({
      queryKey: CHAVES_DE_CONDUTORES.exportacao(recorte),
      queryFn: () =>
        chamarApi<PaginaComPermissoes<Condutor>>(
          `${CAMINHO_DA_API}/exportacao${parametrosDaListagem({ ...recorte })}`,
        ),
      staleTime: 0,
    });

    exportarCsv(
      'condutores.csv',
      ['Nome', 'CPF', 'Preposto', 'CRMC', 'Credenciado', 'Cadastro', 'Situação'],
      resposta.itens.map((condutor) => [
        condutor.nome,
        condutor.cpf ? mascaraCpf(condutor.cpf) : '',
        condutor.eh_preposto ? 'Sim' : 'Não',
        condutor.crmc_numero ?? '',
        condutor.credenciados?.nome ?? '',
        condutor.estado_cadastro ?? '',
        condutor.status,
      ]),
    );

    return resposta.itens.length;
  };
}
